import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SortDirection as SortDirectionEnum } from '@/enums/sort-direction.enum'
import { ToolSortColumn } from '@/enums/tool-sort-column.enum'
import { ToolStatus } from '@/enums/tool-status.enum'
import { TabNavigation } from '@/components/ui'
import { useLoading } from '@/contexts/LoadingContext'
import { toDrawerOptions } from '@/converters/tool-management-table/drawer-option.converter'
import type { Trolley } from '@/hooks/useToolManagement/useGetTrolleys'

import { TableFilters } from './TableFilters'
import { DrawerToolsTable } from './DrawerToolsTable'

interface ToolManagementTableProps {
  trolleys: Trolley[]
  isLoadingTrolleys: boolean
}

export function ToolManagementTable({
  trolleys: apiTrolleys,
  isLoadingTrolleys,
}: ToolManagementTableProps) {
  const { t } = useTranslation()
  const { showLoading, hideLoading } = useLoading()
  const [activeTrolley, setActiveTrolley] = useState<Nullable<string>>(null)
  const [activeDrawer, setActiveDrawer] = useState<ValueOption['value']>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<Nullable<ToolStatus>>(null)
  const [sortColumn, setSortColumn] = useState<Nullable<ToolSortColumn>>(null)
  const [sortDirection, setSortDirection] =
    useState<Nullable<SortDirectionEnum>>(null)

  useEffect(() => {
    if (isLoadingTrolleys) {
      showLoading(t('common.loading'))
    } else {
      hideLoading()
    }
  }, [isLoadingTrolleys, showLoading, hideLoading, t])

  const trolleyNames = apiTrolleys.map(t => t.name)
  const trolleyCounts = apiTrolleys.map(t => t.drawer_count)
  const trolleyDescriptions = apiTrolleys.map(t => t.description)

  useEffect(() => {
    if (apiTrolleys.length === 0) {
      setActiveTrolley(null)
      setActiveDrawer(null)
      return
    }

    const trolleyExists = activeTrolley
      ? apiTrolleys.some(t => t.name === activeTrolley)
      : false

    if (!trolleyExists || !activeTrolley) {
      setActiveTrolley(apiTrolleys[0].name)
    }
  }, [apiTrolleys, activeTrolley])

  const activeTrolleyData = useMemo(() => {
    if (!activeTrolley) return null
    return apiTrolleys.find(t => t.name === activeTrolley) ?? null
  }, [apiTrolleys, activeTrolley])

  const drawerOptions = useMemo(() => {
    if (!activeTrolleyData) return []
    return toDrawerOptions(activeTrolleyData.drawers)
  }, [activeTrolleyData])

  useEffect(() => {
    if (drawerOptions.length === 0) {
      setActiveDrawer(null)
      return
    }

    const drawerExists =
      activeDrawer !== null
        ? drawerOptions.some(option => option.value === activeDrawer)
        : false

    if (!drawerExists || !activeDrawer) {
      setActiveDrawer(drawerOptions[0].value)
    }
  }, [drawerOptions, activeDrawer])

  const handleTrolleySelect = (trolleyName: string) => {
    setActiveTrolley(trolleyName)
    setActiveDrawer(null)
  }

  const handleSort = (column: Nullable<ToolSortColumn>) => {
    if (sortColumn === column) {
      if (sortDirection === SortDirectionEnum.Asc) {
        setSortDirection(SortDirectionEnum.Desc)
      } else if (sortDirection === SortDirectionEnum.Desc) {
        setSortColumn(null)
        setSortDirection(null)
      } else {
        setSortDirection(SortDirectionEnum.Asc)
      }
    } else {
      setSortColumn(column)
      setSortDirection(SortDirectionEnum.Asc)
    }
  }

  return (
    <section className='space-y-6 overflow-x-hidden w-full min-w-0'>
      <TabNavigation
        items={trolleyNames}
        selectedItem={activeTrolley}
        counts={trolleyCounts}
        onItemSelect={handleTrolleySelect}
      />

      {trolleyDescriptions ? (
        <p className='text-sm'>{trolleyDescriptions}</p>
      ) : null}

      <TableFilters
        drawerOptions={drawerOptions}
        selectedDrawer={activeDrawer}
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        onDrawerChange={setActiveDrawer}
        onSearchChange={setSearchTerm}
        onStatusFilterChange={setStatusFilter}
      />
      {activeDrawer && (
        <DrawerToolsTable
          drawerId={activeDrawer}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      )}
    </section>
  )
}
